import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const WhatIsHeFH = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'Understanding HeFH: The Basics Explained',
      duration: '9:15',
      views: '22K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/7f1e9b80aa642e6be1987fcd849e4cd6.jpg',
    },
    {
      title: 'How HeFH Affects Your Arteries Over Time',
      duration: '11:30',
      views: '19K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Medical%20animation%20style%20illustration%20showing%20cholesterol%20plaque%20buildup%20in%20artery%20cross%20section%20with%20LDL%20particles%20accumulating%20on%20vessel%20wall%20educational%20healthcare%20visualization%20warm%20tones%20clean%20background&width=400&height=250&seq=hefh-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Living with HeFH: Treatment and Management',
      duration: '13:20',
      views: '27K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=Patient%20consulting%20with%20specialist%20about%20long%20term%20cholesterol%20management%20plan%20reviewing%20medication%20options%20and%20lifestyle%20changes%20on%20digital%20tablet%20in%20bright%20modern%20cardiology%20clinic%20warm%20natural%20lighting&width=400&height=250&seq=hefh-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Familial Heart Disease Awareness
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              What Is Heterozygous Familial<br />Hypercholesterolemia (HeFH)?
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Heterozygous Familial Hypercholesterolemia (HeFH) is the most common form of inherited high cholesterol, affecting approximately 1 in 250 people worldwide. Learn what it is, how it differs from ordinary high cholesterol, and why early awareness matters for you and your family.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Doctor%20reviewing%20genetic%20cholesterol%20test%20results%20with%20patient%20showing%20DNA%20double%20helix%20and%20lipid%20panel%20data%20on%20screen%20in%20warm%20modern%20cardiology%20office%20professional%20healthcare%20photography%20natural%20lighting%20clean%20aesthetic&width=400&height=300&seq=hefh-blog-card-001&orientation=landscape"
                  alt="Blog articles about HeFH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth guide to understanding HeFH</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Medical%20professional%20presenting%20educational%20video%20about%20inherited%20heart%20disease%20and%20cholesterol%20genetics%20on%20large%20monitor%20screen%20in%20modern%20lecture%20hall%20warm%20professional%20lighting%20healthcare%20education%20setting&width=400&height=300&seq=hefh-video-card-001&orientation=landscape"
                  alt="Educational videos about HeFH"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Visual explainers on HeFH basics</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=detailed%20medical%20infographic%20poster%20about%20heterozygous%20familial%20hypercholesterolemia%20HeFH%20showing%20genetic%20inheritance%20pattern%20with%20family%20tree%20diagram%20cholesterol%20levels%20comparison%20charts%20and%20heart%20disease%20risk%20statistics%20modern%20healthcare%20data%20visualization%20coral%20teal%20and%20cream%20color%20palette%20professional%20medical%20illustration&width=400&height=300&seq=hefh-infograph-card-new-001&orientation=landscape"
                  alt="Heterozygous Familial Hypercholesterolemia (HeFH) infographics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Heterozygous Familial Hypercholesterolemia (HeFH)</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Is It In Your Genes? The Shocking Truth About a Common Cause of Early Heart Attacks
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 12, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  9 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=detailed%20DNA%20double%20helix%20structure%20with%20highlighted%20genetic%20mutations%20affecting%20heart%20health%20and%20cholesterol%20pathways%20medical%20illustration%20showing%20LDLR%20gene%20with%20cardiac%20background%20warm%20coral%20and%20teal%20color%20palette%20professional%20scientific%20visualization&width=800&height=400&seq=hefh-blog-hero-genes&orientation=landscape"
                  alt="Genetic causes of high cholesterol"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  When most of us think about high cholesterol, a familiar picture comes to mind: middle age, a diet high in saturated fats, and a sedentary lifestyle. We've been taught that cholesterol is something we need to start managing later in life, a consequence of our choices over time. But what if this picture is incomplete?
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  What if dangerously high cholesterol could be hardwired into your DNA, present from the moment you are born? Imagine a condition that causes plaque to build up in your arteries from early childhood, regardless of how healthy you eat or how much you exercise. This isn't a hypothetical scenario; it's the reality for an estimated 30 million people worldwide who have Familial Hypercholesterolemia (FH), a genetic disorder that can cause "bad" cholesterol levels to be two to three times higher than normal, right from birth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This condition is called <strong>Familial Hypercholesterolemia (FH)</strong>, and it's one of the most common—and commonly missed—genetic disorders. This article will reveal the most surprising facts about this hidden health threat and why knowing your family's health history is more critical than you might think.
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's Far More Common Than You Think
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While many rare genetic diseases capture public attention, the most common form of FH, <strong>Heterozygous FH (HeFH)</strong>, is surprisingly prevalent, affecting approximately <strong>1 in 250 people worldwide</strong>. This statistic makes FH one of the most common inherited metabolic conditions.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#f04e58]">1:250</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">People Affected Worldwide</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Despite how common it is, the vast majority of people with FH have no idea they are at risk. They may be young, fit, and have no outward signs of a health problem, leaving the condition to silently damage their cardiovascular system for decades. The scale of this diagnostic gap is staggering.
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "More than 90% of people with the condition remain undiagnosed."
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's a Lifelong Condition That Begins at Birth
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Unlike lifestyle-related high cholesterol that develops over time, FH causes extremely high levels of LDL ("bad") cholesterol from birth. This is because a genetic mutation impairs the body's ability to remove LDL cholesterol from the blood. The liver's LDL receptors, which act like docking stations to clear this "bad" cholesterol, are either faulty or insufficient in number.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because of this, the process of atherosclerosis—the buildup of cholesterol plaque in the arteries—doesn't start in middle age; it begins in early childhood. This fundamental difference is why FH is so dangerous.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold mb-2">Early Detection Possible:</p>
                  <p className="text-gray-700 leading-relaxed">
                    While the condition is often discovered in the teenage years or adulthood, the underlying high cholesterol levels can be detected much earlier, with elevated cholesterol being identifiable in children as early as <strong>ages 2 to 5</strong>.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Risk Isn't Just High—It's Exponential
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The lifelong exposure to extremely high LDL cholesterol dramatically accelerates the development of heart disease. For individuals with untreated FH, the risk isn't just slightly elevated; it's on a completely different scale.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#f04e58]">20x</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">Higher Risk of Early Heart Disease</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The data is stark: untreated FH leads to a <strong>20 times higher risk of developing early heart disease</strong>. This isn't a vague, long-term risk. Without treatment, men with the common form of FH often develop heart disease before age 50, and women before age 60.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <p className="text-gray-900 font-semibold mb-2">Homozygous FH (HoFH) - The Severe Form:</p>
                  <p className="text-gray-700 leading-relaxed">
                    For those with the much rarer and more severe form, <strong>Homozygous FH (HoFH)</strong>—where a faulty gene is inherited from both parents, leaving the body with virtually no ability to clear LDL cholesterol—the consequences are even more immediate. In these cases, heart disease can occur in childhood, and life expectancy can be under 30 years without aggressive, modern therapies.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Your Body Might Be Dropping Hints
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While FH is often called a "silent" condition because its primary damage is internal, it can sometimes produce subtle but distinct physical signs. These clues are caused by cholesterol deposits forming in tissues outside of the arteries. Recognizing them can be a crucial first step toward diagnosis.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-4">Key physical signs include:</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0">
                        <i className="ri-hand-heart-line text-[#f04e58]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Xanthomas</h5>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          Waxy, fatty lumps that can appear on tendons underneath the skin, most commonly on the knuckles of the hands or the Achilles tendon at the back of the ankle.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0d9488]/10 flex-shrink-0">
                        <i className="ri-eye-line text-[#0d9488]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Xanthelasmas</h5>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          Yellowish cholesterol deposits that form on or around the eyelids.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0">
                        <i className="ri-eye-2-line text-[#f04e58]"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-1">Corneal Arcus</h5>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          A grey or white ring that forms around the outer edge of the cornea (the clear, front part of the eye). While this can occur in older adults, its appearance in someone under 45 is a strong indicator of FH.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white">
                  <p className="font-semibold mb-2 text-white">Severe Form Warning:</p>
                  <p className="text-white/95 leading-relaxed">
                    In the most severe form of FH (HoFH), these signs can appear alarmingly early; xanthomas, for instance, may be visible before a child is 5 years old.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Knowing Your Family History is Crucial
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  FH is passed down through families in a predictable pattern. The condition follows an <strong>autosomal dominant inheritance pattern</strong>, which means if one of your parents has the faulty gene, you have a <strong>50% chance of inheriting it</strong>. It's a genetic coin flip with every child.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#0d9488]">50%</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">Chance of Inheriting FH</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This is caused by a mutation in one of a few key genes—most commonly <strong>LDLR</strong>, <strong>APOB</strong>, or <strong>PCSK9</strong>—that are critical for cholesterol regulation. This inheritance pattern makes family screening an incredibly powerful and effective tool. If one person in a family is diagnosed with FH, it provides a clear reason to test their parents, siblings, and children.
                </p>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white mb-4">
                  <p className="text-xl font-bold text-white mb-2">
                    "While inheriting the gene is a matter of chance, managing the condition is not."
                  </p>
                  <p className="text-white/95 leading-relaxed">
                    FH is a treatable disorder. With early diagnosis and modern treatments, such as statins and PCSK9 inhibitors, individuals can lower their cholesterol, dramatically reduce their risk, and expect to live a normal lifespan.
                  </p>
                </div>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488] text-white flex-shrink-0">
                    <i className="ri-lightbulb-line text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    A Final Thought
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  Familial Hypercholesterolemia is a common, serious, and largely hidden threat lurking in the gene pool of families across the globe. It fundamentally challenges our conventional wisdom about high cholesterol, proving that it isn't always a disease of lifestyle or old age.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  However, awareness is our most powerful weapon against it. Through understanding the risks, recognizing the signs, and prioritizing family health history, this formidable genetic condition can be found and effectively managed.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold text-lg italic">
                    Given that your genes can play such a powerful role, is it time to look at your family's health history not just as stories, but as a roadmap to your own future health?
                  </p>
                </div>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Protect Your Heart, Protect Your Future</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  Early detection of Familial Hypercholesterolemia can dramatically reduce your risk of heart disease. If you have a family history of high cholesterol or early heart attacks, take the first step toward understanding your genetic risk.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Is HeFH? A Visual Explanation
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to understand how Heterozygous Familial Hypercholesterolemia works, why it&apos;s different from regular high cholesterol, and what it means for you and your family.
              </p>
            </div>

            {/* Video Player Placeholder */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-gray-900 aspect-video mb-8">
              <video
                controls
                autoPlay
                className="w-full h-full object-contain"
                poster="https://readdy.ai/api/search-image?query=Medical%20educational%20video%20thumbnail%20showing%20animated%20heart%20with%20cholesterol%20particles%20and%20DNA%20helix%20explaining%20heterozygous%20familial%20hypercholesterolemia%20genetics%20modern%20cinematic%20healthcare%20visualization%20dark%20background%20with%20coral%20and%20teal%20accent%20lighting&width=800&height=450&seq=hefh-video-poster-main&orientation=landscape"
              >
                <source src="https://rr3---sn-nx57ynsr.googlevideo.com/videoplayback?expire=1771397881&ei=2UaVacWgNJK7n88P2rekgAI&ip=151.186.183.87&id=b4abdc5952d63481&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771390681,&mh=u5&mm=32&mn=sn-nx57ynsr&ms=su&mv=m&mvi=3&pl=26&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=329.212&lmt=1769283988025630&mt=1771390366&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRAIgB9iXFJc6b3ePF4gIE3Wc8V-Ct2QtfXsalhMd7HpOHC0CIASZVJNDZmJVhwOpTkoI_XHqQ8tk2EuWU17tcOQaRTPk&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRQIhAPpqQ8RmAwQRoZE_TXW3W_faUAX2YqLvq7JWeEgiAM_CAiB9jiWy8opS5l2jWN8c940R-IOikI2_IrD09bH_I2c9ew==" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Understanding HeFH Series</h3>
                  <p className="text-sm text-gray-500">Educational Content</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This video covers the genetic basis of HeFH, how it differs from lifestyle-related high cholesterol, the inheritance pattern, key diagnostic criteria, available treatment options, and the importance of cascade screening for family members.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Familial Hypercholesterolemia (FH): Understanding Your Genetic Heart Risk
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual guide covering the two faces of FH, early detection strategies, physical signs, and the critical importance of proactive management.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/99a1283b4941e1d1e2f3ab8e5f3d2109.png"
                  alt="Familial Hypercholesterolemia (FH): Understanding Your Genetic Heart Risk - The Two Faces of FH, Early Detection and Action"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid md:grid-cols-4 gap-4 mb-12">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">1 in 250</div>
                <p className="text-sm text-gray-600">People affected worldwide</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">90%+</div>
                <p className="text-sm text-gray-600">Remain undiagnosed</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#f04e58] mb-2">50%</div>
                <p className="text-sm text-gray-600">Chance passed to children</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm text-center">
                <div className="text-3xl font-bold text-[#0d9488] mb-2">20x</div>
                <p className="text-sm text-gray-600">Higher heart disease risk</p>
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Genetic Inheritance</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  HeFH follows an autosomal dominant pattern. One mutated gene copy from either parent is enough to cause significantly elevated LDL from birth.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-stethoscope-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Clinical Diagnosis</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  DLCN scoring and Simon Broome criteria combine cholesterol levels, family history, and physical signs to assess the probability of HeFH.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-capsule-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Effective Treatment</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Statins, ezetimibe, PCSK9 inhibitors, and newer therapies like inclisiran can reduce LDL dramatically and restore near‑normal life expectancy.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default WhatIsHeFH;
